/** Derive Unified School Management Dashboard view-models from /enrolment/management-bifurcation */

const CORE_GROUPS = ["1_Govt", "2_Aided", "3_Unaided"];
const GROUP_SHORT = { "1_Govt": "Govt", "2_Aided": "Aided", "3_Unaided": "Unaided" };
const ZP_CODES = new Set(["16", "55"]);
const SELF_FINANCE_CODE = "27";
const UNRECOGNIZED_CODE = "8";
const AIDED_VARIANT_CODES = new Set(["25", "53", "54"]);

const num = (v) => Number(v) || 0;
const pct = (part, total) => (total > 0 ? (part / total) * 100 : 0);
const computePtr = (enrolments, teachers) => (teachers > 0 ? enrolments / teachers : null);
const teachersAtPtr = (enrolments, target) => Math.ceil(enrolments / target);

const getGroup = (payload, groupId) => payload?.groups?.find((g) => g.group_id === groupId) || null;

const allDetails = (payload) =>
  (payload?.groups || []).flatMap((g) =>
    (g.rows || [])
      .filter((r) => r.row_type !== "group" && r.mgmt_code !== "—")
      .map((r) => ({
        ...r,
        group_id: g.group_id,
        group_title: g.group_title || GROUP_SHORT[g.group_id] || g.group_label,
      }))
  );

const findDetail = (payload, code) => allDetails(payload).find((r) => String(r.mgmt_code) === String(code));

const sumDetailSchools = (payload, codes) =>
  codes.reduce((sum, code) => sum + num(findDetail(payload, code)?.schools), 0);

const statusForPtr = (ptrValue) => {
  if (ptrValue == null) return { status: "—", tag: "" };
  if (ptrValue >= 40) return { status: "Critical", tag: "red" };
  if (ptrValue >= 31) return { status: "Watch", tag: "" };
  return { status: "Stable", tag: "green" };
};

const statusForDrilldown = (row, { topSchoolsCode, topEnrolCode }) => {
  const ptrValue = row.ptr;
  if (String(row.mgmt_code) === topSchoolsCode) return { status: "Largest Network", tag: "green" };
  if (String(row.mgmt_code) === topEnrolCode && ptrValue >= 35) return { status: "High PTR", tag: "red" };
  if (ptrValue >= 35) return { status: "High PTR", tag: "red" };
  if (String(row.mgmt_code) === UNRECOGNIZED_CODE) return { status: "Recognition Risk", tag: "red" };
  if (row.group_id === "2_Aided" && ptrValue >= 32) return { status: "Watch", tag: "" };
  if (row.group_id === "1_Govt" && ptrValue >= 34) return { status: "Equity Watch", tag: "" };
  if (num(row.enrolments) >= num(row.schools) * 300) return { status: "Major Private Segment", tag: "blue" };
  return { status: "Watch", tag: "" };
};

export const hasUsmData = (payload) => Boolean(payload?.grand_total?.schools);

export const buildUsmDerived = (payload) => {
  if (!hasUsmData(payload)) return null;

  const grand = payload.grand_total || {};
  const totalSchools = num(grand.schools);
  const totalEnrolments = num(grand.enrolments);
  const totalTeachers = num(grand.teachers);
  const overallPtr = grand.ptr ?? computePtr(totalEnrolments, totalTeachers);

  const govt = getGroup(payload, "1_Govt");
  const aided = getGroup(payload, "2_Aided");
  const unaided = getGroup(payload, "3_Unaided");
  const other = getGroup(payload, "4_Other");

  const details = allDetails(payload);
  const topByEnrol = [...details].sort((a, b) => num(b.enrolments) - num(a.enrolments))[0];
  const topBySchools = [...details].sort((a, b) => num(b.schools) - num(a.schools))[0];

  const unrecognized = findDetail(payload, UNRECOGNIZED_CODE);
  const selfFinance = findDetail(payload, SELF_FINANCE_CODE);
  const missingEnrolment = num(payload.schools_without_enrolment);

  const govtSchoolShare = pct(num(govt?.schools), totalSchools);
  const aidedEnrolShare = pct(num(aided?.enrolments), totalEnrolments);
  const highestPtrRow = [...details]
    .filter((r) => r.ptr != null && num(r.teachers) > 0)
    .sort((a, b) => num(b.ptr) - num(a.ptr))[0];
  const shortageAt25 = Math.max(0, teachersAtPtr(totalEnrolments, 25) - totalTeachers);
  const selfFinanceShare = pct(num(selfFinance?.schools), num(unaided?.schools));

  const mixDonut = CORE_GROUPS.map((gid, index) => {
    const g = getGroup(payload, gid);
    const schools = num(g?.schools);
    return {
      label: GROUP_SHORT[gid],
      count: schools,
      pct: Number(pct(schools, totalSchools).toFixed(2)),
      color: index === 0 ? "primary" : index === 1 ? "accent" : "secondary",
    };
  }).filter((d) => d.count > 0);

  const mixComparison = CORE_GROUPS.map((gid) => {
    const g = getGroup(payload, gid);
    if (!g) return null;
    return {
      management: g.group_title || GROUP_SHORT[gid],
      schools: num(g.schools),
      enrolments: num(g.enrolments),
      teachers: num(g.teachers),
      ptr: g.ptr ?? computePtr(num(g.enrolments), num(g.teachers)),
    };
  }).filter(Boolean);

  const avgEnrolment = CORE_GROUPS.map((gid) => {
    const g = getGroup(payload, gid);
    if (!g || !num(g.schools)) return null;
    return {
      value: Math.round(num(g.enrolments) / num(g.schools)),
      sub: GROUP_SHORT[gid],
    };
  }).filter(Boolean);

  const shareFromSummary = (section) => {
    const summary = payload.summary?.find((s) => s.section === section);
    if (!summary?.rows?.length) return [];
    const tones = ["", "green", "orange"];
    return summary.rows.map((row, i) => ({
      label: row.bucket.replace(/\.$/, ""),
      pct: Number(num(row.percentage).toFixed(2)),
      tone: tones[i] || "",
    }));
  };

  const privateSchoolShare = 100 - govtSchoolShare;
  const govtEnrolShare = pct(num(govt?.enrolments), totalEnrolments);
  const privateEnrolShare = 100 - govtEnrolShare;
  const zpSchools = sumDetailSchools(payload, [...ZP_CODES]);
  const zpConcentration = pct(zpSchools, num(govt?.schools));

  const schoolHierarchy = [
    ...CORE_GROUPS.map((gid) => {
      const g = getGroup(payload, gid);
      if (!g) return null;
      const schools = num(g.schools);
      const share = pct(schools, totalSchools);
      return {
        category: g.group_title || GROUP_SHORT[gid],
        schools,
        share: `${share.toFixed(2)}%`,
        status: gid === "1_Govt" ? "Largest Group" : "Major",
        tag: gid === "1_Govt" ? "blue" : "",
      };
    }).filter(Boolean),
    ...[topBySchools, selfFinance]
      .filter(Boolean)
      .filter((row, index, arr) => arr.findIndex((r) => r.mgmt_code === row.mgmt_code) === index)
      .slice(0, 2)
      .map((row) => ({
        category: row.management,
        schools: num(row.schools),
        share: `${pct(num(row.schools), totalSchools).toFixed(2)}%`,
        status: String(row.mgmt_code) === String(topBySchools?.mgmt_code) ? "Largest category" : "Largest unaided subtype",
        tag: String(row.mgmt_code) === String(topBySchools?.mgmt_code) ? "green" : "",
      })),
  ];

  const groupBarRows = (metric) => {
    const total = metric === "schools" ? totalSchools : metric === "enrolments" ? totalEnrolments : totalTeachers;
    const tones = ["", "green", "orange"];
    return CORE_GROUPS.map((gid, i) => {
      const g = getGroup(payload, gid);
      const value = num(g?.[metric]);
      return {
        label: GROUP_SHORT[gid],
        width: pct(value, total),
        value,
        tone: tones[i] || "",
        display: value.toLocaleString("en-IN"),
      };
    });
  };

  const ptrKpis = [
    { label: "Overall PTR", value: overallPtr?.toFixed?.(2) ?? "—", sub: `${payload.scope_label || "Scope"} average` },
    ...CORE_GROUPS.map((gid) => {
      const g = getGroup(payload, gid);
      const ptrValue = g?.ptr ?? computePtr(num(g?.enrolments), num(g?.teachers));
      const band =
        ptrValue == null ? "—" : ptrValue >= 35 ? "Highest pressure" : ptrValue >= 28 ? "Manageable band" : "Comfortable band";
      return {
        label: `PTR – ${GROUP_SHORT[gid]}`,
        value: ptrValue?.toFixed?.(2) ?? "—",
        sub: band,
      };
    }),
  ];

  const ptrWatchlist = [...details]
    .filter((r) => r.ptr != null && num(r.ptr) > 31 && num(r.teachers) > 0)
    .sort((a, b) => num(b.ptr) - num(a.ptr))
    .slice(0, 8)
    .map((r) => {
      const gap = Math.max(0, teachersAtPtr(num(r.enrolments), 30) - num(r.teachers));
      const st = statusForPtr(num(r.ptr));
      return {
        category: r.management,
        ptr: num(r.ptr).toFixed(2),
        gap: gap.toLocaleString("en-IN"),
        status: st.status,
        tag: st.tag,
      };
    });

  const requiredAt25 = teachersAtPtr(totalEnrolments, 25);
  const requiredAt30 = teachersAtPtr(totalEnrolments, 30);
  const aidedGapAt30 = aided
    ? Math.max(0, teachersAtPtr(num(aided.enrolments), 30) - num(aided.teachers))
    : 0;

  const netAt30 = totalTeachers - requiredAt30;
  const ptrAdequacy = [
    {
      id: "required_ptr_25",
      label: "Required Teachers @ PTR 25",
      value: requiredAt25.toLocaleString("en-IN"),
      districtValue: requiredAt25,
    },
    {
      id: "existing_teachers",
      label: "Existing Teachers",
      value: totalTeachers.toLocaleString("en-IN"),
      districtValue: totalTeachers,
    },
    {
      id: "shortage_ptr_25",
      label: "Teacher Shortage Estimate @ PTR 25",
      value: shortageAt25.toLocaleString("en-IN"),
      districtValue: shortageAt25,
    },
    {
      id: "required_ptr_30",
      label: "Required Teachers @ PTR 30",
      value: requiredAt30.toLocaleString("en-IN"),
      districtValue: requiredAt30,
    },
    {
      id: "net_position_ptr_30",
      label: "Net Position @ PTR 30",
      value:
        netAt30 >= 0
          ? `${netAt30.toLocaleString("en-IN")} surplus system-wide`
          : `${Math.abs(netAt30).toLocaleString("en-IN")} short system-wide`,
      districtValue: netAt30,
      districtLabel:
        netAt30 >= 0
          ? `${netAt30.toLocaleString("en-IN")} surplus system-wide`
          : `${Math.abs(netAt30).toLocaleString("en-IN")} short system-wide`,
    },
    {
      id: "aided_pressure",
      label: "Aided Pressure",
      value: aided
        ? `Needs ${aidedGapAt30.toLocaleString("en-IN")} more teachers @ PTR 30`
        : "No aided group in scope",
      districtValue: aidedGapAt30,
      districtLabel: aided
        ? `Needs ${aidedGapAt30.toLocaleString("en-IN")} more teachers @ PTR 30`
        : "No aided group in scope",
    },
  ];

  const drilldownRows = [...details]
    .sort((a, b) => num(b.enrolments) - num(a.enrolments))
    .slice(0, 12)
    .map((r) => {
      const schools = num(r.schools);
      const enrolments = num(r.enrolments);
      const teachers = num(r.teachers);
      const ptrValue = r.ptr ?? computePtr(enrolments, teachers);
      const st = statusForDrilldown(r, {
        topSchoolsCode: topBySchools?.mgmt_code,
        topEnrolCode: topByEnrol?.mgmt_code,
      });
      return {
        code: r.mgmt_code,
        name: r.management,
        group: r.group_title,
        schools: schools.toLocaleString("en-IN"),
        enrolments: enrolments.toLocaleString("en-IN"),
        teachers: teachers.toLocaleString("en-IN"),
        ptr: ptrValue?.toFixed?.(2) ?? "—",
        avg: schools > 0 ? Math.round(enrolments / schools) : "—",
        status: st.status,
        tag: st.tag,
      };
    });

  const aidedPtr = aided?.ptr ?? computePtr(num(aided?.enrolments), num(aided?.teachers));
  const lowEnrolmentSchools = num(payload.schools_with_low_enrolment_1_to_5);
  const zeroTeacherSchools = num(payload.zero_teacher_schools);
  const risks = [
    {
      id: "aided_teacher_pressure",
      risk: "Aided teacher pressure",
      trigger: "PTR > 35",
      parameterLabel: "PTR",
      value: aidedPtr != null ? aidedPtr.toFixed(2) : "—",
      owner: "Teacher Planning",
      status: aidedPtr != null && aidedPtr > 35 ? "High" : "Watch",
      tag: aidedPtr != null && aidedPtr > 35 ? "red" : "",
    },
    {
      id: "low_enrolment",
      risk: "Schools with Low Enrollment",
      trigger: "Students 1–5 only",
      parameterLabel: "Students",
      value: lowEnrolmentSchools.toLocaleString("en-IN"),
      owner: "School Management",
      status: lowEnrolmentSchools > 0 ? "High" : "Stable",
      tag: lowEnrolmentSchools > 0 ? "red" : "green",
    },
    {
      id: "zero_teacher",
      risk: "Low Staff",
      trigger: "Zero Teacher School",
      parameterLabel: "Teachers",
      value: zeroTeacherSchools.toLocaleString("en-IN"),
      owner: "Teacher Planning",
      status: zeroTeacherSchools > 0 ? "High" : "Stable",
      tag: zeroTeacherSchools > 0 ? "red" : "green",
    },
  ];

  const labelCleanupCount = details.filter((r) => AIDED_VARIANT_CODES.has(String(r.mgmt_code))).length;
  const mappedGroups = (payload.groups || []).filter((g) => num(g.schools) > 0).length;

  const kpiFilled = [
    totalSchools,
    totalEnrolments,
    totalTeachers,
    overallPtr,
    govtSchoolShare,
    aidedEnrolShare,
    highestPtrRow?.ptr,
    shortageAt25,
    selfFinanceShare,
    num(unrecognized?.schools),
    totalSchools,
    overallPtr,
    mixDonut.length,
    mixComparison.length,
    shareFromSummary("School").length,
    ptrWatchlist.length,
    drilldownRows.length,
    missingEnrolment,
    mappedGroups,
    risks.length,
  ].filter((v) => v != null && v !== 0 && v !== "—").length;

  const coveragePct = Math.min(100, Math.round((kpiFilled / 20) * 100));

  const storyParts = [];
  if (govt && aided) {
    storyParts.push(
      num(govt.schools) >= num(aided.schools)
        ? `${govt.group_title || "Govt"} owns the highest number of schools`
        : `${aided.group_title || "Aided"} leads on school count`
    );
    storyParts.push(
      num(aided.enrolments) >= num(govt.enrolments)
        ? `${aided.group_title || "Aided"} carries the highest number of students`
        : `${govt.group_title || "Govt"} carries the highest number of students`
    );
  }
  if (aidedPtr != null) {
    storyParts.push(
      aidedPtr >= 30
        ? `${aided?.group_title || "Aided"} has the highest PTR pressure (${aidedPtr.toFixed(2)})`
        : `PTR pressure is relatively balanced across management groups`
    );
  }
  if (selfFinance) {
    storyParts.push(`Self Finance represents ${selfFinanceShare.toFixed(2)}% of unaided schools in scope`);
  }

  return {
    scopeLabel: payload.scope_label || "District scope",
    coverage: `${kpiFilled}/20`,
    coveragePct,
    hero: {
      title: "Unified KPI Coverage",
      description: `Live management bifurcation for ${payload.scope_label || "the selected scope"} — schools, enrolments, teachers, PTR and data-quality signals derived from imported enrolment and teacher records.`,
      badges: [
        { text: `${mappedGroups} management groups mapped`, tone: "" },
        {
          text: aidedPtr != null && aidedPtr >= 30 ? "Aided PTR requires action" : "PTR within review band",
          tone: aidedPtr != null && aidedPtr >= 30 ? "amber" : "",
        },
        { text: `${payload.scope_label || "District"} scope`, tone: "" },
      ],
      story: storyParts.join(", ") + ".",
      kpis: [
        { label: "Total Schools", value: totalSchools.toLocaleString("en-IN"), sub: "School base in scope" },
        { label: "Total Enrolments", value: totalEnrolments.toLocaleString("en-IN"), sub: "Across all managements" },
        { label: "Total Teachers", value: totalTeachers.toLocaleString("en-IN"), sub: "Teacher strength in scope" },
        { label: "Overall PTR", value: overallPtr?.toFixed?.(2) ?? "—", sub: "Scope average" },
        {
          label: "Largest Enrolment Category",
          value: topByEnrol?.management || "—",
          sub: topByEnrol ? `${num(topByEnrol.enrolments).toLocaleString("en-IN")} students` : "—",
        },
        {
          label: "Largest School Category",
          value: topBySchools?.management || "—",
          sub: topBySchools ? `${num(topBySchools.schools).toLocaleString("en-IN")} schools` : "—",
        },
      ],
    },
    prioritySnapshot: [
      { label: "Govt School Share", value: `${govtSchoolShare.toFixed(2)}%`, sub: `${num(govt?.schools).toLocaleString("en-IN")} schools` },
      { label: "Aided Enrolment Share", value: `${aidedEnrolShare.toFixed(2)}%`, sub: "Largest student-serving segment" },
      {
        label: "Highest PTR Category",
        value: highestPtrRow?.ptr?.toFixed?.(2) ?? "—",
        sub: highestPtrRow?.management || "—",
      },
      { label: "Teacher Shortage Estimate", value: shortageAt25.toLocaleString("en-IN"), sub: "@ target PTR 25" },
      { label: "Self Finance School Share", value: `${selfFinanceShare.toFixed(2)}%`, sub: "Within unaided schools" },
      { label: "Unrecognized School Count", value: num(unrecognized?.schools).toLocaleString("en-IN"), sub: "Recognition watchlist" },
      { label: "Bifurcation Schools", value: totalSchools.toLocaleString("en-IN"), sub: "Scope total" },
      { label: "Bifurcation PTR", value: overallPtr?.toFixed?.(2) ?? "—", sub: "Scope average" },
    ],
    managementMix: {
      donut: mixDonut,
      comparison: mixComparison.map((r) => ({
        ...r,
        schools: r.schools.toLocaleString("en-IN"),
        enrolments: r.enrolments.toLocaleString("en-IN"),
        teachers: r.teachers.toLocaleString("en-IN"),
        ptr: r.ptr?.toFixed?.(2) ?? "—",
      })),
      avgEnrolment,
    },
    shareAnalysis: {
      schoolShare: shareFromSummary("School"),
      enrolmentShare: shareFromSummary("Enrolment"),
      teacherShare: shareFromSummary("Teachers"),
      cards: [
        {
          label: "Public vs Private School Share",
          value: `${govtSchoolShare.toFixed(2)}% : ${privateSchoolShare.toFixed(2)}%`,
          sub: "Govt vs Aided+Unaided",
        },
        {
          label: "Public vs Private Enrolment Share",
          value: `${govtEnrolShare.toFixed(2)}% : ${privateEnrolShare.toFixed(2)}%`,
          sub: "Govt vs Aided+Unaided",
        },
        { label: "ZP Concentration", value: `${zpConcentration.toFixed(2)}%`, sub: "Within Govt schools" },
        { label: "Self Finance Concentration", value: `${selfFinanceShare.toFixed(2)}%`, sub: "Within Unaided schools" },
      ],
    },
    schoolHierarchy,
    enrolmentView: {
      bars: groupBarRows("enrolments"),
      stats: [
        {
          label: "Largest Enrolment Category",
          value: topByEnrol ? `${topByEnrol.management} · ${num(topByEnrol.enrolments).toLocaleString("en-IN")}` : "—",
        },
        {
          label: "Largest Unaided Category",
          value: selfFinance
            ? `${selfFinance.management} · ${num(selfFinance.enrolments).toLocaleString("en-IN")}`
            : "—",
        },
        {
          label: "Govt School Average Size",
          value:
            govt && num(govt.schools) > 0
              ? `${Math.round(num(govt.enrolments) / num(govt.schools))} students / school`
              : "—",
        },
      ],
    },
    teachersView: {
      bars: groupBarRows("teachers"),
      stats: CORE_GROUPS.map((gid) => {
        const g = getGroup(payload, gid);
        const avg = g && num(g.schools) > 0 ? (num(g.teachers) / num(g.schools)).toFixed(2) : "—";
        return { label: "Avg Teachers / School", value: `${GROUP_SHORT[gid]} ${avg}` };
      }),
    },
    ptrKpis,
    ptrWatchlist,
    ptrAdequacy,
    drilldownRows,
    risks,
    actionQueue: [
      {
        num: 1,
        tone: "purple",
        title: "Reduce Aided PTR Pressure",
        text: aidedPtr != null && aidedPtr >= 30
          ? `Prioritize ${aided?.group_title || "Aided"} and high-pressure subcategories (current PTR ${aidedPtr.toFixed(2)}) for teacher deployment review.`
          : "Review teacher deployment where PTR exceeds the scope average.",
      },
      {
        num: 2,
        tone: "",
        title: "Resolve Recognition and Data Quality Gaps",
        text: `Review unrecognized schools and validate the ${missingEnrolment.toLocaleString("en-IN")} schools without enrolment record in the bifurcation view.`,
      },
      {
        num: 3,
        tone: "orange",
        title: "Monitor Concentration Risk",
        text: `Track dependency on ${topBySchools?.management || "top school categories"} and ${topByEnrol?.management || "top enrolment categories"} through monthly management-share reviews.`,
      },
    ],
    dataQuality: {
      kpis: [
        { label: "Mapped Management Groups", value: `${mappedGroups}`, sub: "Govt, Aided, Unaided, Other" },
        { label: "Schools Without Enrolment Record", value: missingEnrolment.toLocaleString("en-IN"), sub: missingEnrolment > 0 ? "Needs follow-up" : "No gap detected" },
        { label: "Unrecognized School Count", value: num(unrecognized?.schools).toLocaleString("en-IN"), sub: "Compliance watchlist" },
        { label: "Potential Label Cleanup Items", value: `${labelCleanupCount}`, sub: "Private Aided 20/40/60 etc." },
      ],
      checks: [
        { check: "Management mapping", description: "Each management code maps consistently to Govt / Aided / Unaided / Other.", status: mappedGroups >= 3 ? "Track" : "Pending", tag: mappedGroups >= 3 ? "blue" : "" },
        { check: "Duplicate labels", description: "Review Private Aided 20, 40 and 60 categories for end-user readability.", status: labelCleanupCount > 0 ? "Pending" : "Routine", tag: labelCleanupCount > 0 ? "" : "green" },
        { check: "Missing enrolment records", description: `Validate and backfill the ${missingEnrolment.toLocaleString("en-IN")} Other schools with zero enrolment.`, status: missingEnrolment > 0 ? "High priority" : "Routine", tag: missingEnrolment > 0 ? "red" : "green" },
        { check: "PTR validation", description: "PTR is computed as enrolment ÷ teachers for every management row.", status: "Routine", tag: "green" },
        { check: "Recognition status", description: "Review unrecognized management records for governance reporting.", status: num(unrecognized?.schools) > 0 ? "Pending" : "Routine", tag: num(unrecognized?.schools) > 0 ? "" : "green" },
      ],
      publishNotes: [
        { label: "Scope", value: payload.scope_label || "District scope" },
        { label: "School Base", value: `${totalSchools.toLocaleString("en-IN")} schools in bifurcation` },
        { label: "Teacher Shortage Estimate", value: `Shown at target PTR 25 (${shortageAt25.toLocaleString("en-IN")} teachers)` },
        { label: "Self Finance Share", value: `${selfFinanceShare.toFixed(2)}% of unaided schools` },
        { label: "Data Source", value: "Enrolment analytics joined with teacher analytics by UDISE" },
      ],
    },
    raw: payload,
  };
};

/** Rule-based markdown fallback for Mistral insights on the unified school management dashboard. */
export const buildUsmMarkdown = (derived) => {
  if (!derived) return "";

  const { hero, risks, actionQueue, prioritySnapshot } = derived;
  const lines = [
    "## Insights",
    `- ${hero.story}`,
    ...prioritySnapshot.slice(0, 5).map((k) => `- ${k.label}: ${k.value} (${k.sub})`),
    "",
    "## Root Cause Signals",
    ...(risks.length
      ? risks.map((r) => `- ${r.risk}: ${r.trigger} = ${r.value} — ${r.status}`)
      : ["- No risk thresholds triggered for this scope."]),
    "",
    "## Recommendations",
    ...actionQueue.map((a) => `- ${a.title}: ${a.text}`),
    "",
    "## Priority Action Items",
    ...actionQueue.map((a) => `- ${a.title}: ${a.text}`),
  ];

  return lines.join("\n");
};
